// @flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import classnames from 'classnames';
import { compose } from 'recompose';
import { translate } from 'react-i18next';

import { collapsibleListCardFactory } from './CollapsibleListCard';
import { listCardFactory } from './ListCard';
import Icon from 'common/components/core/icon';

import NumberUtils from 'common/utils/NumberUtils';
import theme from './LocationCard.scss';
import { SpanLoader, DivLoader, Loader } from 'common/components/core/loader';
import BackgroundColor from 'common/components/core/colors/background.scss';
import { getMarkerColorFromConfidence } from 'common/components/core/util/MarkerColorUtil';
import {
    Button,
    ButtonWithPopover,
    ButtonTypes
} from 'common/components/core/button';
import LocationCardSearchBox from './LocationCardSearchBox';
import CustomStringUtil from 'common/components/core/util/CustomStringUtil';
import VerifyButton from 'common/entity/generic/VerifyButton';
import { OverflowDetectorPopover } from 'common/components/core/popover/OverflowDetectorPopover';
import CopyButton from 'common/components/core/button/CopyButton';
import { dashboardColors } from 'common/enums/ColorEnum';
import { SizeEnum } from 'common/enums/UIEnums';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import Checkbox from 'common/components/core/checkbox';
import {
    PermissionMatch,
    RoleOperationEnum,
    AccessDeniedMessage
} from 'common/modules/rbac';
import { Tooltip } from '@locus-taxy/neo-ui';

const renderUndoBtn = props => {
    const { t, theme, isResetEnabled, onReset, isExpanded } = props;
    if (!isResetEnabled) {
        return null;
    }
    return (
        <ButtonWithPopover
            theme={theme}
            buttonType={ButtonTypes.Solid}
            onClick={onReset}
            disabled={isExpanded ? false : true}
            icon="undo"
            popover
            triggerPopoverOn={PopoverTriggerEnum.Hover}
            popOverContent={t('undo')}
            place={PopoverPlaceEnum.Right}
            className={theme.undoBtn}
        />
    );
};
const Title = ({
    theme,
    title,
    isLoading,
    onVerify,
    subText,
    isVerified,
    isEditInProgress,
    isVerifyInProgress,
    t
}) => {
    return (
        <DivLoader loading={isLoading} className={theme.titleWrap}>
            <div className={theme.titleLeft}>
                <div className={theme.title}>
                    <OverflowDetectorPopover
                        overflowText={title}
                        className={theme.titleText}
                    >
                        {title}
                    </OverflowDetectorPopover>
                    {isEditInProgress ? (
                        <div className={theme.editLoader}>
                            <Loader active theme={theme} />
                        </div>
                    ) : null}
                </div>
                {subText ? (
                    <OverflowDetectorPopover
                        overflowText={subText}
                        className={theme.subText}
                    >
                        {subText}
                    </OverflowDetectorPopover>
                ) : null}
            </div>
            {onVerify ? (
                <PermissionMatch
                    operations={RoleOperationEnum.UPDATE_LOCATIONS}
                    // eslint-disable-next-line react/jsx-no-bind
                    render={hasPermission => (
                        <Tooltip
                            shouldWrapChildren
                            tooltip={
                                hasPermission ? null : (
                                    <AccessDeniedMessage action={t('verify')} />
                                )
                            }
                        >
                            <VerifyButton
                                disabled={!hasPermission}
                                theme={theme}
                                onVerify={onVerify}
                                isVerified={isVerified}
                                isVerifyInProgress={isVerifyInProgress}
                            />
                        </Tooltip>
                    )}
                />
            ) : null}
        </DivLoader>
    );
};

const stopEventPropagation = e => e.stopPropagation();

const renderLatLng = ({
    accuracyText,
    finalConfidenceColor,
    latLngText,
    latLngIcon,
    theme,
    onLatLngClick,
    t,
    isResetEnabled,
    onReset,
    isExpanded
}) => {
    if (!latLngText && !accuracyText && !isResetEnabled) {
        return null;
    }
    return (
        <div className={theme.latLng}>
            {latLngText ? (
                <>
                    <Icon
                        icon={latLngIcon}
                        theme={theme}
                        className={theme.latLngIcon}
                    />
                    <span className={theme.latLngText}>{latLngText}</span>
                </>
            ) : null}
            {onLatLngClick ? (
                <Button
                    buttonType={ButtonTypes.Link}
                    semibold
                    color={dashboardColors.darkSkyBlue.name}
                    onClick={onLatLngClick}
                    label={t('geocodeMap.copyToGroup')}
                    theme={theme}
                    className={theme.latLngButton}
                />
            ) : latLngText ? (
                <CopyButton
                    theme={theme}
                    onClick={stopEventPropagation}
                    copySuccessTimeout={5000}
                    copyContent={latLngText}
                    copyTooltip={t('geocodeMap.copyLatLng')}
                    className={theme.copyContentButton}
                />
            ) : null}

            {accuracyText ? (
                <div
                    className={classnames(theme.accuracyText, {
                        [theme[finalConfidenceColor]]: !!theme[
                            finalConfidenceColor
                        ]
                    })}
                >
                    {accuracyText}
                </div>
            ) : null}
            {renderUndoBtn({
                isResetEnabled,
                t,
                theme,
                onReset,
                isExpanded
            })}
        </div>
    );
};
const Subtitle = ({ theme, subtitle, t, isExpanded, latLngProps }) => {
    const { latLngText, accuracyText, ...otherLatLngProps } = latLngProps;
    if (!subtitle && !latLngText && !accuracyText) {
        return null;
    }
    return (
        <div className={theme.subtitleWrap}>
            {subtitle ? (
                <div className={theme.subtitle} title={subtitle}>
                    {subtitle}
                </div>
            ) : null}
            {renderLatLng({
                ...otherLatLngProps,
                latLngText,
                accuracyText,
                t,
                theme,
                isExpanded
            })}
        </div>
    );
};

const LeftIcon = ({
    originalConfidenceColor,
    leftText,
    leftIconBgColor,
    theme,
    isLoading,
    onToggleCard,
    isCardChecked
}) => {
    if (!leftText) {
        return null;
    }
    return (
        <SpanLoader loading={isLoading} className={theme.leftIconWrap}>
            {onToggleCard ? (
                <div onClick={stopEventPropagation}>
                    {/*
                    HACK: this is needed because stopEventPropagation
                    doesn't work on checkox events diretly;
                    so it needs to be wrapped in a div
                */}
                    <Checkbox
                        smallCheckbox
                        checked={isCardChecked}
                        theme={theme}
                        className={theme.cardCheckbox}
                        onChange={onToggleCard}
                    />
                </div>
            ) : null}

            <Icon
                theme={theme}
                className={classnames(theme.leftIcon)}
                bgColor={leftIconBgColor}
                numberLabel={leftText}
            />
            {originalConfidenceColor ? (
                <span
                    className={classnames(
                        theme.confidenceIcon,
                        BackgroundColor[originalConfidenceColor]
                    )}
                />
            ) : null}
        </SpanLoader>
    );
};

const ExtraContent = props => {
    const {
        onApplySystemGeocode,
        t,
        applySystemGeocodeLabel = t('geocodeMap.applySystemGeocode'),
        theme
    } = props;
    if (!onApplySystemGeocode) {
        return null;
    }
    return (
        <Button
            theme={theme}
            className={theme.applySystemGeocodeBtn}
            onClick={onApplySystemGeocode}
            buttonType={ButtonTypes.Link}
            color={dashboardColors.darkSkyBlue.name}
            uppercase
            underline
            semibold
            size={SizeEnum.Small}
            label={applySystemGeocodeLabel}
        />
    );
};
const ListCard = listCardFactory({ Title, Subtitle, ExtraContent, LeftIcon });
const locationCardFactory = ({
    CardComponent = ListCard,
    CollapseComponent = LocationCardSearchBox
}) => {
    const CollapsibleListCard = collapsibleListCardFactory({
        CardComponent,
        CollapseComponent
    });

    class LocationCard extends React.PureComponent {
        handleClick = event => {
            const { locationEntity, onCardClick, disabled } = this.props;
            if (onCardClick) {
                onCardClick({ locationEntity, disabled }, event);
            }
        };

        handleLatLngClick = (...args) => {
            const { onLatLngClick, locationEntity } = this.props;
            onLatLngClick && onLatLngClick({ locationEntity }, ...args);
        };

        handleAddressChange = (lat, lng) => {
            const { onAddressChange, locationEntity } = this.props;
            onAddressChange({
                latLng: { lat, lng },
                locationEntity
            });
        };

        onReset = event => {
            const { onReset, locationEntity } = this.props;
            onReset && onReset({ locationEntity }, event);
        };

        handleVerify = (...args) => {
            const { onVerify, locationEntity } = this.props;
            onVerify && onVerify({ locationEntity }, ...args);
        };
        handleCardToggle = (...args) => {
            const { onToggleCard, locationEntity } = this.props;
            onToggleCard && onToggleCard({ locationEntity }, ...args);
        };
        onApplySystemGeocode = event => {
            const { onApplySystemGeocode, locationEntity } = this.props;

            onApplySystemGeocode && onApplySystemGeocode(locationEntity, event);
        };
        getSearchLabel() {
            const {
                t,
                isEditInProgress,
                isNonGeocoded,
                originalConfidence,
                currentConfidence
            } = this.props;
            if (isEditInProgress) {
                return t('loading_ellipsis');
            }
            if (isNonGeocoded) {
                return t('unableToLocate');
            }
            if (originalConfidence) {
                return t('locationChanged');
            }

            return t(
                CustomStringUtil.locationConfidenceToInputLabelKey(
                    currentConfidence
                )
            );
        }
        render() {
            const {
                theme,
                index,
                label,
                address,
                isSelected,
                isExpanded,
                currentConfidence,
                isEditable,
                isLoading,
                originalConfidence,
                customerName,
                customerContact,
                shouldShowOriginalColor,
                style,
                className,
                subText,
                isVerified,
                isVerifyInProgress,
                isEditInProgress,
                onVerify,
                latLngText,
                latLngIcon,
                autoFocusSearchBox,
                showLeftText,
                locationEntity,
                onLatLngClick,
                t,
                geocodeVerifiedGeometry,
                isValidateGeocodesActive,
                onApplySystemGeocode,
                applySystemGeocodeLabel,
                isResetEnabled,
                onToggleCard,
                isCardChecked,
                ...others
            } = this.props;
            // icon background
            const currentConfidenceColor = getMarkerColorFromConfidence(
                currentConfidence,
                shouldShowOriginalColor
            );
            //if edited, then show the dot
            const originalConfidenceColor = originalConfidence
                ? getMarkerColorFromConfidence(
                      originalConfidence,
                      shouldShowOriginalColor
                  )
                : null;
            const searchLabel = this.getSearchLabel();
            const searchLabelColor = originalConfidenceColor
                ? 'dark-sky-blue'
                : currentConfidenceColor;
            let accuracyText;
            let finalConfidenceColor = currentConfidenceColor;
            let isApplySystemGeocodeVisible = false;
            if (geocodeVerifiedGeometry && isValidateGeocodesActive) {
                isApplySystemGeocodeVisible = locationEntity.getIsApplySystemGeocodeVisible();
                accuracyText = geocodeVerifiedGeometry.getAccuracyText();
                finalConfidenceColor = geocodeVerifiedGeometry.getAccuracyConfidenceColor();
            }
            return (
                <CollapsibleListCard
                    {...others}
                    isEditable={isEditable}
                    onToggleCard={onToggleCard && this.handleCardToggle}
                    isCardChecked={isCardChecked}
                    t={t}
                    style={style}
                    autoFocusSearchBox={autoFocusSearchBox}
                    originalConfidenceColor={originalConfidenceColor}
                    leftIconBgColor={finalConfidenceColor}
                    onChange={this.handleAddressChange}
                    theme={theme}
                    onClick={this.handleClick}
                    title={label}
                    subtitle={address}
                    searchLabel={searchLabel}
                    searchLabelColor={searchLabelColor}
                    leftText={showLeftText && NumberUtils.padLeft(index)}
                    isExpandable={isEditable}
                    isSelected={isSelected}
                    isExpanded={isExpanded}
                    isLoading={isLoading}
                    className={classnames(className, {
                        [theme.originalConfidenceHighlight]:
                            currentConfidenceColor === originalConfidenceColor,
                        [theme.isLoading]: isLoading
                    })}
                    inputLabel={''}
                    customerName={customerName}
                    customerContact={customerContact}
                    subText={subText}
                    onVerify={onVerify && this.handleVerify}
                    isVerified={isVerified}
                    isVerifyInProgress={isVerifyInProgress}
                    isEditInProgress={isEditInProgress}
                    locationEntity={locationEntity}
                    latLngProps={{
                        accuracyText,
                        latLngText,
                        finalConfidenceColor,
                        latLngIcon,
                        onReset: this.onReset,
                        isResetEnabled: isEditable && isResetEnabled,
                        onLatLngClick: onLatLngClick && this.handleLatLngClick
                    }}
                    onApplySystemGeocode={
                        isApplySystemGeocodeVisible &&
                        onApplySystemGeocode &&
                        this.onApplySystemGeocode
                    }
                    applySystemGeocodeLabel={applySystemGeocodeLabel}
                />
            );
        }
    }
    LocationCard.defaultProps = {
        showLeftText: true,
        latLngIcon: 'location-icon'
    };
    const ThemedLocationCard = compose(
        themr('LocationCard', theme),
        translate('translations')
    )(LocationCard);
    return ThemedLocationCard;
};
const LocationCard = locationCardFactory({});
export { LocationCard, locationCardFactory };
export default LocationCard;
